import React, { Component, useRef } from 'react';
import Select, { components, createFilter } from 'react-select';
import i18n from 'utils/translate';
import './styles.scss';
import { Tooltip } from 'react-tippy';
import { useHasOverflow } from '../../hooks/useHasOverflow';

const defaultProps = {
  label: null,
  options: [],
  onChange: () => {},
  onBlur: () => {},
  defaultValue: null,
  value: null,
  isDisabled: false,
  customFilterLogic: false,
  searchFromLeft: true,
  isMulti: false,
  closeMenuOnSelect: true,
  required: false,
  isSearchable: true,
  isFormSubmitted: false
};

// Performance improvement:
// Trim the options to specified value if exceeded
function MenuListOverride(props) {
  const trimTo = 300;
  const children = React.useMemo(() => React.Children.toArray(props.children), [props.children]);
  return (
    <components.MenuList {...props}>
      {children.length > trimTo ? children.slice(0, trimTo) : props.children}
    </components.MenuList>
  );
}

function MultiValueLabelOverride(props) {
  const ref = useRef();
  const hasOverflow = useHasOverflow(ref);

  const label = (
    <div {...props.innerProps} ref={ref}>
      {props.children}
    </div>
  );

  return hasOverflow ? (
    <Tooltip
      title={props.data.label}
      className={`multi-select-label-wrapper`}
      arrow={true}
      trigger="mouseenter click"
      animation="fade"
    >
      {label}
    </Tooltip>
  ) : (
    label
  );
}

const customStyles = {
  control: props => ({
    ...props,
    boxShadow: 'none'
  }),
  option: provided => ({
    ...provided,
    cursor: 'pointer'
  })
};

class FilterField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      requiredError: false,
      isDirty: false
    };
  }

  static isValueValid(value) {
    return Array.isArray(value) ? value.length > 0 : !!value || value === 0;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value, required, isFormSubmitted } = nextProps;
    const { isDirty, requiredError } = prevState;
    const isNextValueValid = FilterField.isValueValid(value);

    // When continuing to next step or field has been touched
    // error state is set for invalid values if required
    if (isFormSubmitted || (isDirty && !isNextValueValid)) {
      return { requiredError: isNextValueValid ? false : required };
    }

    // We need to reset requiredError for resolved values
    if ((isDirty || isFormSubmitted) && isNextValueValid && required && requiredError) {
      return { requiredError: false };
    }

    return null;
  }

  customFilter(option, input) {
    if (!input) {
      return true;
    }
    if (this.isInt(input)) {
      return option.data.data.zip.toLowerCase().startsWith(input.toLowerCase());
    } else {
      return option.data.data.community.toLowerCase().startsWith(input.toLowerCase());
    }
  }

  isInt(value) {
    var x;
    return isNaN(value) ? !1 : ((x = parseFloat(value)), (0 | x) === x);
  }

  getErrorResponse = () => {
    const { requiredError } = this.state;
    const { isDisabled } = this.props;
    let error;

    if (requiredError && !isDisabled) {
      error = 'Eingabe erforderlich!';
    }

    return <span className="field-error-message">{i18n(error)}</span>;
  };

  render() {
    let selectValue = null;

    const { requiredError } = this.state;
    const { className, defaultValue, value, isDisabled } = this.props;

    const isError = requiredError && !isDisabled;

    if (defaultValue || value || value === 0) {
      const { isMulti, options } = this.props;
      const val = value !== null ? value : defaultValue;

      let filteredOptions = [];

      if (isMulti && Array.isArray(val)) {
        filteredOptions = options.filter(item => val.includes(item.value));
        if (filteredOptions.length) {
          selectValue = filteredOptions.map(item => {
            return { value: item.value, label: item.customLabel || item.label };
          });
        }
      } else {
        filteredOptions = options.filter(item => {
          return item.value === val;
        });
        if (filteredOptions.length) {
          selectValue = filteredOptions[0];
        }
      }
    }

    const filterConfig = { matchFrom: this.props.searchFromLeft ? 'start' : 'any', ignoreAccents: false };
    const { options, onChange, onBlur, isMulti, closeMenuOnSelect, isSearchable, customFilterLogic, label } =
      this.props;
    const { isDirty } = this.state;

    const selectProps = {
      components: { MenuList: MenuListOverride, MultiValueLabel: MultiValueLabelOverride },
      options,
      onChange: event => {
        if (onChange) onChange(event);

        // The field has been modified
        if (!isDirty) {
          this.setState({
            isDirty: true
          });
        }
      },
      onBlur,
      placeholder: i18n('select-placeholder'),
      value: selectValue,
      isDisabled,
      isClearable: 'true',
      styles: customStyles,
      className: isError ? 'filterfield-error' : '',
      noOptionsMessage: () => i18n('no-option-suggested'),
      theme: theme => ({
        ...theme,
        borderRadius: '3px',
        colors: {
          ...theme.colors,
          primary: '#017a3d',
          primary75: '#419b6e',
          primary50: '#80bc9e',
          primary25: '#bfdece'
        }
      }),
      isMulti,
      closeMenuOnSelect,
      tabSelectsValue: false,
      isSearchable
    };
    if (customFilterLogic) {
      selectProps.filterOption = this.customFilter.bind(this);
    } else {
      selectProps.filterOption = createFilter(filterConfig);
    }

    return (
      <div className={`responsive-field ${className || ''} filterfield-wrapper ${isError ? 'filterfield-error' : ''}`}>
        <label className="responsive-field__label">{label}</label>
        <div className="input-wrapper">
          <Select {...selectProps} />
        </div>
        {this.getErrorResponse()}
      </div>
    );
  }
}

FilterField.defaultProps = defaultProps;

export default FilterField;
